import timezones from "@/assets/timezones.json";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatTimezoneDisplay, groupTimezones } from "@/lib/timezone-utils";
import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";
import { RRulesEditor } from "./rrules-editor";

interface ScheduleFormProps {
  name: string;
  timezone: string;
  rrules: string;
  onNameChange: (value: string) => void;
  onTimezoneChange: (value: string) => void;
  onRrulesChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting: boolean;
}

// Get browser's current timezone as default
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// Get all available timezones from the JSON file
const ALL_TIMEZONES = Object.keys(timezones).sort();

export const ScheduleForm = ({
  name,
  timezone,
  rrules,
  onNameChange,
  onTimezoneChange,
  onRrulesChange,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
}: ScheduleFormProps) => {
  const { t } = useTranslation("schedules");
  // Group timezones by offset
  const groupedTimezones = useMemo(() => groupTimezones(ALL_TIMEZONES), []);
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">{t("form.scheduleName")}</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={t("form.scheduleNamePlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <RRulesEditor value={rrules} onChange={onRrulesChange} />
      </div>

      <Accordion type="single" collapsible>
        <AccordionItem value="advanced">
          <AccordionTrigger>{t("form.advanced")}</AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-2">
              <Label htmlFor="timezone">{t("form.timezone")}</Label>
              <Select value={timezone} onValueChange={onTimezoneChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  {groupedTimezones.map((group) => (
                    <SelectGroup key={group.canonical}>
                      <SelectItem value={group.canonical}>
                        {formatTimezoneDisplay(group.canonical, group.offset)}
                        <span className="text-muted-foreground">
                          {group.alternatives.length > 0 && ", "}
                          {group.alternatives.slice(0, 3).join(", ")}
                          {group.alternatives.length > 3 &&
                            ` ${t("form.moreTimezones", { count: group.alternatives.length - 3 })}`}
                        </span>
                      </SelectItem>
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("form.timezoneHint")}
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t("form.cancel")}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("form.saving") : submitLabel}
        </Button>
      </div>
    </form>
  );
};

// Export helper function for getting browser timezone
export { getBrowserTimezone };
